import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    RiMicFill,
    RiMicOffFill,
    RiPhoneFill,
    RiWifiLine,
    RiLoader4Line,
} from 'react-icons/ri';

import { SimulationProvider, useSimulation } from '../../context/SimulationContext';
import { simulationService } from '../../services/simulationService';
import type { CreateSimulationData } from '../../services/simulationService';
import GlassButton from '../../components/ui/v2/GlassButton';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// ---------------------------------------------------------------------------
// Persona Avatar — elegant minimal tile
// ---------------------------------------------------------------------------

function PersonaAvatar({ name, role, isSpeaking }: { name: string; role: string; isSpeaking: boolean }) {
    const initial = name ? name[0].toUpperCase() : '?';
    return (
        <div className="flex-1 flex flex-col items-center justify-center gap-6">
            {/* Avatar */}
            <div className="relative">
                {/* Outer glow ring when speaking */}
                <AnimatePresence>
                    {isSpeaking && (
                        <motion.div
                            key="ring"
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.85 }}
                            className="absolute inset-0 rounded-full border-2 border-white/40 animate-ping"
                            style={{ margin: '-8px' }}
                        />
                    )}
                </AnimatePresence>
                <div className={`w-32 h-32 rounded-full flex items-center justify-center text-5xl font-bold transition-all duration-300 ${isSpeaking ? 'bg-white text-black' : 'bg-white/10 text-white border border-white/20'}`}>
                    {initial}
                </div>
                {/* Speaking indicator dot */}
                {isSpeaking && (
                    <div className="absolute -bottom-1 -right-1 flex gap-0.5 items-end h-5 p-1 bg-black rounded-full border border-white/20">
                        {[1, 1.6, 1, 1.6, 1].map((h, i) => (
                            <motion.div
                                key={i}
                                className="w-1 bg-white rounded-full"
                                animate={{ scaleY: [h, h === 1 ? 1.6 : 1, h] }}
                                transition={{ duration: 0.4, repeat: Infinity, delay: i * 0.08 }}
                                style={{ height: '4px', transformOrigin: 'bottom' }}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Name + role */}
            <div className="text-center">
                <div className="text-xl font-semibold text-white">{name || 'Interviewer'}</div>
                <div className="text-sm text-gray-400">{role || 'AI Interviewer'}</div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// User Tile
// ---------------------------------------------------------------------------

function UserTile({ isMicMuted, isSpeaking }: { isMicMuted: boolean; isSpeaking: boolean }) {
    return (
        <div className="flex flex-col items-center gap-3">
            <div className={`relative w-20 h-20 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${isSpeaking ? 'border-blue-400 bg-blue-500/10' : 'border-white/10 bg-white/5'}`}>
                {/* Mic icon or muted */}
                {isMicMuted ? (
                    <RiMicOffFill className="text-2xl text-red-400" />
                ) : (
                    <RiMicFill className={`text-2xl ${isSpeaking ? 'text-blue-400' : 'text-gray-400'}`} />
                )}
                {/* Speaking bars */}
                {isSpeaking && !isMicMuted && (
                    <div className="absolute -bottom-2 flex gap-0.5 items-end h-3">
                        {[1, 1.5, 2, 1.5, 1].map((h, i) => (
                            <motion.div
                                key={i}
                                className="w-1 bg-blue-400 rounded-full"
                                animate={{ scaleY: [h * 0.5, h, h * 0.5] }}
                                transition={{ duration: 0.3, repeat: Infinity, delay: i * 0.06 }}
                                style={{ height: '4px', transformOrigin: 'bottom' }}
                            />
                        ))}
                    </div>
                )}
            </div>
            <span className="text-xs text-gray-500">{isMicMuted ? 'Muted' : isSpeaking ? 'Speaking...' : 'You'}</span>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Timer display
// ---------------------------------------------------------------------------

function TimerDisplay({ elapsed, limit }: { elapsed: number; limit: number }) {
    const remaining = Math.max(0, limit - elapsed);
    const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
    const ss = String(remaining % 60).padStart(2, '0');
    const isLow = remaining < 120;
    return (
        <span className={`font-mono font-bold text-lg tabular-nums ${isLow ? 'text-red-400' : 'text-white'}`}>
            {mm}:{ss}
        </span>
    );
}

// ---------------------------------------------------------------------------
// Connection status badge
// ---------------------------------------------------------------------------

function ConnBadge({ state }: { state: string }) {
    const map: Record<string, { label: string; color: string }> = {
        CONNECTING: { label: 'Connecting', color: 'text-yellow-400' },
        CONNECTED: { label: 'Live', color: 'text-green-400' },
        ERROR: { label: 'Connection error', color: 'text-red-400' },
        ENDED: { label: 'Ended', color: 'text-gray-400' },
        IDLE: { label: 'Initialising', color: 'text-gray-400' },
    };
    const { label, color } = map[state] ?? { label: state, color: 'text-gray-400' };
    return (
        <div className={`flex items-center gap-1.5 text-xs font-medium ${color}`}>
            {state === 'CONNECTING' || state === 'IDLE'
                ? <RiLoader4Line className="animate-spin" />
                : <span className="w-1.5 h-1.5 rounded-full bg-current" />}
            {label}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Room Content (uses context)
// ---------------------------------------------------------------------------

function SimulationRoomContent({ simulationId }: { simulationId: string }) {
    const navigate = useNavigate();
    const {
        connectionState, aiSpeaking, userSpeaking,
        aiCaptionText, elapsedSeconds, timeLimitSeconds,
        isMicMuted, initSession, toggleMic, endSession,
    } = useSimulation();

    const [confirmEndOpen, setConfirmEndOpen] = useState(false);
    const [isEnding, setIsEnding] = useState(false);
    const [personaInfo, setPersonaInfo] = useState<{ name: string; role: string }>({ name: '', role: '' });

    // On mount — try sessionStorage (came via setup), else call /resume
    useEffect(() => {
        console.log(`[Simulation] Room Effect Triggered: simId=${simulationId}`);
        const cached = sessionStorage.getItem(`sim_session_${simulationId}`);
        if (cached) {
            try {
                const data: CreateSimulationData = JSON.parse(cached);
                setPersonaInfo({ name: data.persona?.name ?? '', role: data.persona?.role ?? '' });
                initSession(data);
                return;
            } catch (err) {
                console.warn('[Simulation] Failed to parse cached session:', err);
            }
        }

        // Resume flow (page refresh / direct navigation)
        simulationService.resume(simulationId, {
            client_state: { last_known_time_used_seconds: 0 },
        }).then(data => {
            if (data.status === 'completed') {
                navigate(`/simulation-result/${simulationId}`, { replace: true });
                return;
            }
            const active = data as any;
            setPersonaInfo({ name: active.persona?.name ?? '', role: active.persona?.role ?? '' });
            initSession({
                ...active,
                time_used_seconds: active.time_used_seconds ?? 0,
                transcript_context: active.transcript_context ?? [],
            });
        }).catch(err => {
            console.error('[Resume] failed:', err);
        });
    }, [simulationId, initSession, navigate]);

    const handleEndConfirm = async () => {
        setIsEnding(true);
        try {
            const id = await endSession();
            if (id) navigate(`/simulation-result/${id}`);
            else navigate('/dashboard');
        } finally {
            setIsEnding(false);
            setConfirmEndOpen(false);
        }
    };

    return (
        <div className="flex flex-col h-screen bg-[#080810] text-white overflow-hidden">

            {/* ── Header ── */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 shrink-0">
                <div className="flex items-center gap-4">
                    <ConnBadge state={connectionState} />
                    <div className="w-px h-4 bg-white/10" />
                    <div className="flex items-center gap-2">
                        <RiWifiLine className="text-gray-500 text-sm" />
                        <TimerDisplay elapsed={elapsedSeconds} limit={timeLimitSeconds} />
                        <span className="text-gray-600 text-xs">remaining</span>
                    </div>
                </div>

                <div className="text-sm text-gray-400 font-medium">
                    AI Interview Simulation
                </div>

                <GlassButton
                    variant="danger"
                    className="flex items-center gap-2 px-4 py-2 text-sm"
                    onClick={() => setConfirmEndOpen(true)}
                >
                    <RiPhoneFill className="text-base" />
                    End Interview
                </GlassButton>
            </div>

            {/* ── Main Stage ── */}
            <div className="flex-1 flex items-center justify-center gap-16 min-h-0 px-8">
                {/* AI Persona (large) */}
                <PersonaAvatar
                    name={personaInfo.name}
                    role={personaInfo.role}
                    isSpeaking={aiSpeaking}
                />

                {/* Divider */}
                <div className="w-px h-40 bg-white/5 shrink-0" />

                {/* User (compact) */}
                <UserTile isMicMuted={isMicMuted} isSpeaking={userSpeaking} />
            </div>

            {/* ── Caption Bar ── */}
            <div className="shrink-0 min-h-[72px] flex items-center justify-center text-center px-12 py-3">
                <AnimatePresence mode="wait">
                    {aiCaptionText ? (
                        <motion.p
                            key="caption"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="text-lg text-white/80 max-w-2xl leading-relaxed"
                        >
                            {aiCaptionText}
                        </motion.p>
                    ) : connectionState === 'CONNECTED' ? (
                        <motion.p
                            key="listening"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-sm text-gray-600"
                        >
                            {isMicMuted ? 'Microphone muted' : 'Listening...'}
                        </motion.p>
                    ) : null}
                </AnimatePresence>
            </div>

            {/* ── Controls ── */}
            <div className="shrink-0 flex items-center justify-center gap-6 py-6 border-t border-white/5">
                {/* Mic toggle */}
                <button
                    onClick={toggleMic}
                    className={`w-14 h-14 rounded-full flex items-center justify-center text-xl transition-all border ${isMicMuted ? 'bg-red-500/20 border-red-500/40 text-red-400 hover:bg-red-500/30' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`}
                    title={isMicMuted ? 'Unmute microphone' : 'Mute microphone'}
                >
                    {isMicMuted ? <RiMicOffFill /> : <RiMicFill />}
                </button>

                {/* End call */}
                <button
                    onClick={() => setConfirmEndOpen(true)}
                    className="h-14 px-8 rounded-full flex items-center gap-2 bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 transition-all font-medium"
                >
                    <RiPhoneFill />
                    End Interview
                </button>
            </div>

            {/* ── End Confirm Dialog ── */}
            <ConfirmDialog
                isOpen={confirmEndOpen}
                title="End Interview?"
                message="This will finalise your session and kick off AI analysis of your performance. You won't be able to continue after ending."
                confirmText={isEnding ? 'Ending...' : 'End & See Results'}
                cancelText="Keep Going"
                variant="danger"
                onCancel={() => setConfirmEndOpen(false)}
                onConfirm={handleEndConfirm}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page entry
// ---------------------------------------------------------------------------

export default function SimulationPage() {
    const { id } = useParams();
    if (!id) return <div className="h-screen bg-black flex items-center justify-center text-white">Invalid simulation ID</div>;

    return (
        <SimulationProvider>
            <SimulationRoomContent simulationId={id} />
        </SimulationProvider>
    );
}
